import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src="/brand/nci-logo-white.png"
                alt="New Creative Industries"
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
              A boutique event experience company. Quality over quantity,
              every time.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/newcreativesa"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                aria-label="New Creative Industries on Facebook"
                className="w-10 h-10 bg-white/5 hover:bg-[#47c3d6]/20 border border-white/10 hover:border-[#47c3d6] rounded-full flex items-center justify-center transition-all duration-300"
              >
                <Facebook size={18} className="text-white" aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/newcreativesa"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                aria-label="New Creative Industries on Instagram"
                className="w-10 h-10 bg-white/5 hover:bg-[#47c3d6]/20 border border-white/10 hover:border-[#47c3d6] rounded-full flex items-center justify-center transition-all duration-300"
              >
                <Instagram size={18} className="text-white" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-[0.2em] text-sm mb-6">
              Quick Links
            </h3>
            <nav aria-label="Quick links" className="flex flex-col gap-4">
              <Link
                to="/"
                data-testid="footer-link-home"
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm"
              >
                Home
              </Link>
              <Link
                to="/services"
                data-testid="footer-link-services"
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm"
              >
                Services
              </Link>
              <Link
                to="/about"
                data-testid="footer-link-about"
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm"
              >
                About Us
              </Link>
              <Link
                to="/#book"
                data-testid="footer-link-contact"
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm"
              >
                Contact
              </Link>
            </nav>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-[0.2em] text-sm mb-6">
              Our Services
            </h3>
            <nav aria-label="Services" className="flex flex-col gap-4">
              <Link
                to="/services#entertain"
                data-testid="footer-link-entertainment"
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm"
              >
                Entertainment Bookings
              </Link>
              <Link
                to="/services#technical"
                data-testid="footer-link-technical"
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm"
              >
                Technical Production
              </Link>
              <Link
                to="/services#led"
                data-testid="footer-link-led"
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm"
              >
                LED Screens
              </Link>
              <Link
                to="/services#streaming"
                data-testid="footer-link-streaming"
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm"
              >
                Live Streaming
              </Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-[0.2em] text-sm mb-6">
              Contact Us
            </h3>
            <div className="flex flex-col gap-4">
              <a
                href="mailto:info@newcreative.co.za"
                data-testid="footer-email"
                className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors text-sm group"
              >
                <Mail size={16} className="text-[#47c3d6] group-hover:text-[#47debd] transition-colors" />
                info@newcreative.co.za
              </a>
              <a
                href="tel:+27638060581"
                data-testid="footer-phone"
                className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors text-sm group"
              >
                <Phone size={16} className="text-[#47c3d6] group-hover:text-[#47debd] transition-colors" />
                063 806 0581
              </a>
              <a
                href="https://wa.me/27638060581"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors text-sm group"
              >
                <MessageCircle size={16} className="text-[#47c3d6] group-hover:text-[#47debd] transition-colors" aria-hidden="true" />
                WhatsApp us
              </a>
              <div className="flex items-start gap-3 text-[#A3A3A3] text-sm">
                <MapPin size={16} className="text-[#47c3d6] mt-0.5 flex-shrink-0" />
                <span>
                  244 Vonkprop Road,
                  <br />
                  Pretoria, Gauteng 0167
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#A3A3A3] text-sm">
            &copy; {currentYear} New Creative Industries. All Rights Reserved.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-[#A3A3A3] text-xs uppercase tracking-[0.2em]">
              In partnership with
            </span>
            <a
              href="https://mspromoters.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-partner-msp"
              className="opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/partners/motorsport-promoters-white.png"
                alt="MotorSport Promoters"
                width="119"
                height="32"
                loading="lazy"
                className="h-8 w-auto"
              />
            </a>
          </div>
          <p className="text-[#A3A3A3] text-xs">
            Crafted with passion in Pretoria, South Africa
          </p>
        </div>
      </div>
    </footer>
  );
};
